import erc721Abi from '@avantarte/smart-contracts/artifacts/@openzeppelin/contracts/token/ERC721/ERC721.sol/ERC721.json';
import erc1155Abi from '@avantarte/smart-contracts/artifacts/@openzeppelin/contracts/token/ERC1155/ERC1155.sol/ERC1155.json';
import multiReleaseAbi from '@avantarte/smart-contracts/artifacts/contracts/implementations/MultiRelease.sol/MultiRelease.json';
import {
  createEthersRpcProvider,
  normalizeChainIdAsNumber,
} from '@avantarte/smart-contracts/utilities/createEthersRpcProvider';
import { captureException } from '@sentry/nextjs';
import * as ethcall from 'ethcall';
import { BigNumber, constants, providers, utils } from 'ethers';

import {
  GeneratedBlockchainNftTokenInfo,
  GeneratedSpecificVoucherResponse,
} from '../../types/generated';
import {
  NFTFromVoucherData,
  NFTMetadata,
  NftTokenInfo,
} from '../../types/web3/nft';
import { captureError } from '../capture';
import { getNftMetadataFromUri } from './nftUtils';

export const createEthCallProvider = async (provider: providers.Provider) => {
  const multiCallProvider = new ethcall.Provider();
  await multiCallProvider.init(provider);
  return multiCallProvider;
};

// TODO: this needs to move to the blockchain service and not be called through the provider directly.
export const fetchNFTFromVoucherData = async (
  { voucher }: GeneratedSpecificVoucherResponse,
  chainId: string,
  nftContractAddress: string,
  multiReleaseContractAddress: string,
): Promise<NFTFromVoucherData | null> => {
  try {
    const chainNumber = normalizeChainIdAsNumber(chainId);
    const tokenNumber = BigNumber.from(voucher.tokenId);
    const voucherId = BigNumber.from(voucher.voucherId);

    const provider = createEthersRpcProvider({
      chainId: chainNumber,
      infura: {
        projectId: process.env.NEXT_PUBLIC_INFURA_PROJECT_ID!,
      },
    });

    const multiCallProvider = new ethcall.Provider();
    await multiCallProvider.init(provider);
    const erc721Contract = new ethcall.Contract(
      nftContractAddress,
      erc721Abi.abi,
    );
    const erc1155Contract = new ethcall.Contract(
      nftContractAddress,
      erc1155Abi.abi,
    );
    const multiReleaseContract = new ethcall.Contract(
      multiReleaseContractAddress,
      multiReleaseAbi.abi,
    );

    const [tokenURI, ownerOf, name, symbol, erc1155TokenURI, voucherClaimer] =
      await multiCallProvider.tryAll<string>([
        erc721Contract.tokenURI(tokenNumber),
        erc721Contract.ownerOf(tokenNumber),
        erc721Contract.name(),
        erc721Contract.symbol(),
        erc1155Contract.uri(tokenNumber),
        multiReleaseContract.usedVouchers(voucherId),
      ]);

    const tokenMetadataURI =
      tokenURI ||
      (erc1155TokenURI
        ? erc1155TokenURI.replace(
            '{id}',
            utils.hexlify(utils.zeroPad(tokenNumber.toHexString(), 64)),
          )
        : '');

    let metadata: NFTMetadata | undefined;

    try {
      metadata = tokenMetadataURI
        ? await getNftMetadataFromUri(tokenMetadataURI)
        : undefined;
    } catch {
      console.error(`failed fetching metadata from ${tokenMetadataURI}`);
    }

    return {
      claimer: voucherClaimer || constants.AddressZero,
      isClaimed: Boolean(
        voucherClaimer && voucherClaimer !== constants.AddressZero,
      ),
      owner: ownerOf,
      contractName: name,
      symbol,
      tokenId: tokenNumber.toNumber(),
      metadata: metadata || null,
      type: 'ERC721',
    };
  } catch (err) {
    captureError(err);
    return null;
  }
};

export const resolvedBlockchainNftTokenInfo = async (
  tokens: GeneratedBlockchainNftTokenInfo[],
): Promise<NftTokenInfo[]> => {
  if (tokens.length === 0) {
    return [];
  }
  return Promise.all(
    tokens?.map(async (t) => {
      try {
        if (!t.metadataUri) {
          return {
            id: t.id,
            owner: t.owner,
            metadataUri: t.metadataUri,
            error: new Error('missing uri'),
          } as NftTokenInfo;
        }
        return {
          id: t.id,
          owner: t.owner,
          metadataUri: t.metadataUri,
          metadata: await getNftMetadataFromUri(t.metadataUri),
        };
      } catch (err) {
        captureException(err, {
          extra: t,
        });
        return {
          id: t.id,
          owner: t.owner,
          metadataUri: t.metadataUri,
          error: err,
        } as NftTokenInfo;
      }
    }),
  );
};
